/* @flow */

import { DidomiSDK } from '@didomi/react';
import * as React from 'react';
import { connect } from 'react-redux';

import type { BasicFunction } from '../../helpers/jsHelpers/types';
import type { SettingValue } from '../../helpers/settings/types';
import type { NETGEM_API_V8_URL_DEFINITION } from '../../libs/netgemLibrary/v8/types/NtgVideoFeed';
import type { CombinedReducers } from '../../redux/reducers';
import type { Dispatch } from '../../redux/types/types';
import type { CARD_DATA_MODAL_TYPE } from '../modal/cardModal/CardModalConstsAndTypes';

import DidomiWrapper from '../../helpers/consent/didomi';
import EpgManager from '../../helpers/epg/epgManager';
import { updateSmartBannerTag } from '../../helpers/jsHelpers/metaTag';
import { isGoogleBot } from '../../helpers/jsHelpers/robots';
import { StorageKeys } from '../../helpers/localStorage/keys';
import LocalStorageManager from '../../helpers/localStorage/localStorageManager';
import Nielsen from '../../helpers/mediametrie/Nielsen';
import { Messenger, MessengerEvents } from '../../helpers/messenger';
import ParentalControlManager from '../../helpers/parentalControl/parentalControlManager';
import { isProfilePrivate } from '../../helpers/profiles/helpers';
import ProfilesManager from '../../helpers/profiles/profilesManager';
import { Setting } from '../../helpers/settings/types';
import { ignoreIfAborted } from '../../libs/netgemLibrary/helpers/cancellablePromise/promiseHelper';
import { DEFAULT_RECORDINGS_STATUS_ITEM } from '../../libs/netgemLibrary/v8/types/Npvr';
import { FEATURE_NPVR, FEATURE_TV } from '../../redux/appConf/constants';
import { RegistrationType } from '../../redux/appRegistration/types/types';
import { showCardModal, showKeyboardShortcutsModal, showLegalNoticeModal, showProfileSplashscreenModal, showProfilesModal } from '../../redux/modal/actions';
import { getParentalControl } from '../../redux/netgemApi/actions/personalData/parentalControl';
import { getPlayingHistory } from '../../redux/netgemApi/actions/personalData/playingHistory';
import { getViewingHistory } from '../../redux/netgemApi/actions/personalData/viewingHistory';
import { getWishlist } from '../../redux/netgemApi/actions/personalData/wishlist';
import { sendV8HubRequest } from '../../redux/netgemApi/actions/v8/hub';
import { getAllRecordings } from '../../redux/netgemApi/actions/v8/recordings';
import { getAllScheduledRecordings } from '../../redux/netgemApi/actions/v8/scheduledRecordings';
import getFavoriteList from '../../redux/netgemApi/actions/videofutur/favoriteList';
import { getPurchaseList } from '../../redux/netgemApi/actions/videofutur/helpers/purchaseList';
import { updateExistingRecordings, updateFutureRecordings, updateScheduledRecordings } from '../../redux/npvr/actions';
import { clearBOFavoriteList, clearBOPurchaseList, setDeeplink, updateSetting, updateWholePlayingHistory, updateWholeViewingHistory, updateWishlist } from '../../redux/ui/actions';
import MainLayout from './MainLayout';
import './MainView.css';

// Wait 1 s before actually refreshing personal data when switching avenue (in ms)
const REFRESH_TIMEOUT = 1_000;

type ReduxMainReducerStateType = {
  +dataCollectionColdUrl: ?NETGEM_API_V8_URL_DEFINITION,
  +dataCollectionHotUrl: ?NETGEM_API_V8_URL_DEFINITION,
  +defaultSubtitles: string,
  +didomiApiKey: string,
  +didomiNoticeId: string,
  +focusedAvenueIndex: number,
  +hubMaxAge: ?number,
  +isBORunning: boolean,
  +isNpvrEnabled: boolean,
  +isRegisteredAsGuest: boolean,
  +isTVEnabled: boolean,
};

type ReduxMainDispatchToPropsType = {
  +localClearExistingRecordings: BasicFunction,
  +localClearFavoriteList: BasicFunction,
  +localClearFutureRecordings: BasicFunction,
  +localClearPurchaseList: BasicFunction,
  +localClearScheduledRecordings: BasicFunction,
  +localClearPlayingHistory: BasicFunction,
  +localClearViewingHistory: BasicFunction,
  +localClearWishList: BasicFunction,
  +localGetAllRecordings: (signal?: AbortSignal) => Promise<void>,
  +localGetAllScheduledRecordings: (signal?: AbortSignal) => Promise<void>,
  +localGetFavoriteList: (signal?: AbortSignal) => Promise<void>,
  +localGetParentalControl: (signal?: AbortSignal) => Promise<void>,
  +localGetPlayingHistory: (signal?: AbortSignal) => Promise<void>,
  +localGetPurchaseList: (signal?: AbortSignal) => Promise<void>,
  +localGetViewingHistory: (signal?: AbortSignal) => Promise<void>,
  +localGetWishlist: (signal?: AbortSignal) => Promise<void>,
  +localSendV8HubRequest: () => Promise<void>,
  +localShowProfilesModal: BasicFunction,
  +localShowProfileSplashscreenModal: BasicFunction,
  +localUpdateDeeplink: (deeplink: string) => void,
  +localUpdateSetting: (setting?: Setting, value: SettingValue) => void,
  +showCard: (cardData: CARD_DATA_MODAL_TYPE) => void,
  +showKeyboardShortcuts: BasicFunction,
  +showLegalNotice: BasicFunction,
};

type MainPropType = {};

type CompleteMainPropType = {
  ...MainPropType,
  ...ReduxMainReducerStateType,
  ...ReduxMainDispatchToPropsType,
};

type MainStateType = {
  isReady: boolean,
};

class MainView extends React.PureComponent<CompleteMainPropType, MainStateType> {
  abortController: AbortController;

  hubRefreshTimer: TimeoutID | null;

  refreshTimer: TimeoutID | null;

  constructor(props: CompleteMainPropType) {
    super(props);

    this.abortController = new AbortController();
    this.hubRefreshTimer = null;
    this.refreshTimer = null;

    this.state = { isReady: false };

    const {
      location: { href, origin, search },
    } = window;

    if (search) {
      const { localUpdateDeeplink } = props;
      localUpdateDeeplink(href);
      updateSmartBannerTag(href);

      if (!isGoogleBot()) {
        // Don't clean up address bar for Google bots
        window.history.pushState({ path: origin }, '', origin);
      }
    }

    const { isTVEnabled } = props;
    if (isTVEnabled) {
      // Start EPG retrieval and refresh mechanism
      EpgManager.initializeCachedFeed();
    } else {
      EpgManager.disable();
    }

    ProfilesManager.preloadAvatars();
  }

  async componentDidMount() {
    const { dataCollectionColdUrl, dataCollectionHotUrl, isRegisteredAsGuest } = this.props;

    Messenger.on(MessengerEvents.CONSENT_OPEN_SETTINGS, this.openConsentSettings);
    Messenger.on(MessengerEvents.CONSENT_RESET, this.resetConsent);
    Messenger.on(MessengerEvents.REFRESH_HUB, this.refreshHub);
    Messenger.on(MessengerEvents.REFRESH_NPVR, this.refreshNpvr);
    Messenger.on(MessengerEvents.REFRESH_PARENTAL_CONTROL, this.refreshParentalControl);
    Messenger.on(MessengerEvents.REFRESH_PERSONAL_DATA, this.refreshPersonalData);
    Messenger.on(MessengerEvents.REFRESH_PLAYING_HISTORY, this.refreshPlayingHistory);
    Messenger.on(MessengerEvents.REFRESH_PURCHASE_LIST, this.refreshPurchaseList);
    Messenger.on(MessengerEvents.REFRESH_VIEWING_HISTORY, this.refreshViewingHistory);
    Messenger.on(MessengerEvents.REFRESH_WISH_LIST, this.refreshWishList);
    Messenger.on(MessengerEvents.CLEAR_PURCHASE_LIST, this.clearPurchaseList);
    Messenger.on(MessengerEvents.CLEAR_PLAYING_HISTORY, this.clearPlayingHistory);
    Messenger.on(MessengerEvents.CLEAR_VIEWING_HISTORY, this.clearViewingHistory);
    Messenger.on(MessengerEvents.CLEAR_WISH_LIST, this.clearWishList);
    Messenger.on(MessengerEvents.CLEAR_NPVR, this.clearNpvr);
    Messenger.on(MessengerEvents.OPEN_CARD, this.openCard);
    Messenger.on(MessengerEvents.SHOW_KEYBOARD_SHORTCUTS, this.showKeyboardShortcuts);
    Messenger.on(MessengerEvents.SHOW_LEGAL_NOTICE, this.showLegalNotice);

    this.startHubRefreshTimer();

    this.refreshNpvr();
    this.refreshPersonalData();

    if (isRegisteredAsGuest) {
      ParentalControlManager.unlock(false);
    }

    if (dataCollectionColdUrl && dataCollectionHotUrl) {
      // Retrieve from local storage and flush data collection messages at startup
      Messenger.emit(MessengerEvents.POP_COLLECTOR);
    }

    this.checkDefaultSubtitles();

    await this.checkProfiles();

    this.setState({ isReady: true });
  }

  componentDidUpdate(prevProps: CompleteMainPropType) {
    const { focusedAvenueIndex, hubMaxAge } = this.props;
    const { focusedAvenueIndex: prevFocusedAvenueIndex, hubMaxAge: prevHubMaxAge } = prevProps;

    if (hubMaxAge !== prevHubMaxAge) {
      // New hub expiration: setup a new refresh timer
      this.startHubRefreshTimer();
    }

    if (focusedAvenueIndex !== prevFocusedAvenueIndex) {
      // Avenue change: refresh stuff
      this.refreshPersonalData();
    }
  }

  componentWillUnmount() {
    const { abortController, refreshTimer } = this;

    abortController.abort('Component MainView will unmount');

    Messenger.off(MessengerEvents.CONSENT_OPEN_SETTINGS, this.openConsentSettings);
    Messenger.off(MessengerEvents.CONSENT_RESET, this.resetConsent);
    Messenger.off(MessengerEvents.REFRESH_HUB, this.refreshHub);
    Messenger.off(MessengerEvents.REFRESH_NPVR, this.refreshNpvr);
    Messenger.off(MessengerEvents.REFRESH_PARENTAL_CONTROL, this.refreshParentalControl);
    Messenger.off(MessengerEvents.REFRESH_PERSONAL_DATA, this.refreshPersonalData);
    Messenger.off(MessengerEvents.REFRESH_PLAYING_HISTORY, this.refreshPlayingHistory);
    Messenger.off(MessengerEvents.REFRESH_PURCHASE_LIST, this.refreshPurchaseList);
    Messenger.off(MessengerEvents.REFRESH_VIEWING_HISTORY, this.refreshViewingHistory);
    Messenger.off(MessengerEvents.REFRESH_WISH_LIST, this.refreshWishList);
    Messenger.off(MessengerEvents.CLEAR_PURCHASE_LIST, this.clearPurchaseList);
    Messenger.off(MessengerEvents.CLEAR_PLAYING_HISTORY, this.clearPlayingHistory);
    Messenger.off(MessengerEvents.CLEAR_VIEWING_HISTORY, this.clearViewingHistory);
    Messenger.off(MessengerEvents.CLEAR_WISH_LIST, this.clearWishList);
    Messenger.off(MessengerEvents.CLEAR_NPVR, this.clearNpvr);
    Messenger.off(MessengerEvents.OPEN_CARD, this.openCard);
    Messenger.off(MessengerEvents.SHOW_LEGAL_NOTICE, this.showLegalNotice);

    this.resetHubRefreshTimer();

    if (refreshTimer) {
      clearTimeout(refreshTimer);
      this.refreshTimer = null;
    }
  }

  checkProfiles = async () => {
    const { isRegisteredAsGuest, localShowProfilesModal, localShowProfileSplashscreenModal } = this.props;

    if (!ProfilesManager.isEnabled || isRegisteredAsGuest) {
      return;
    }

    // Trigger the first profiles load, in order to have the current profile ready
    await ProfilesManager.getProfiles();

    // Check startup profile first
    const startupProfile = ProfilesManager.startupProfile;
    if (startupProfile !== null) {
      // Select startup profile, whether it's private or not
      ProfilesManager.setCurrentProfile(startupProfile.id);
      localShowProfileSplashscreenModal();
      return;
    }

    // Then last used profile
    const lastProfile = ProfilesManager.lastProfile;

    if (lastProfile !== null && !isProfilePrivate(lastProfile)) {
      // Select last used profile, only if it's not private
      ProfilesManager.setCurrentProfile(lastProfile.id);
      localShowProfileSplashscreenModal();
      return;
    }

    // Otherwise open profile selection modal
    Messenger.once(MessengerEvents.MODAL_CLOSED, localShowProfileSplashscreenModal);
    localShowProfilesModal();
  };

  checkDefaultSubtitles = () => {
    const { defaultSubtitles, localUpdateSetting } = this.props;

    if (!defaultSubtitles) {
      // App does not have any default subtitles
      return;
    }

    const defaultSubtitlesApplied = LocalStorageManager.loadBoolean(StorageKeys.DefaultSubtitlesApplied, false);
    if (defaultSubtitlesApplied) {
      // Default subtitles are only applied (i.e. forced) once
      return;
    }

    localUpdateSetting(Setting.AutoSelectSubtitlesTrack, true);
    localUpdateSetting(Setting.LastSubtitlesTrack, defaultSubtitles);
    LocalStorageManager.save(StorageKeys.DefaultSubtitlesApplied, true);
  };

  startHubRefreshTimer = () => {
    const { hubMaxAge } = this.props;

    if (typeof hubMaxAge === 'number') {
      this.hubRefreshTimer = setTimeout(this.refreshHub, hubMaxAge);
    }
  };

  resetHubRefreshTimer = () => {
    const { hubRefreshTimer } = this;

    if (hubRefreshTimer) {
      clearTimeout(hubRefreshTimer);
      this.hubRefreshTimer = null;
    }
  };

  refreshHub = () => {
    const { localSendV8HubRequest } = this.props;

    this.resetHubRefreshTimer();
    localSendV8HubRequest().then(this.startHubRefreshTimer);
  };

  refreshNpvr = () => {
    const { isNpvrEnabled, isRegisteredAsGuest, localGetAllRecordings, localGetAllScheduledRecordings } = this.props;

    if (isNpvrEnabled && !isRegisteredAsGuest) {
      this.refreshPersonalDataInternal(localGetAllRecordings);
      this.refreshPersonalDataInternal(localGetAllScheduledRecordings);
    }
  };

  refreshPurchaseList = () => {
    const { isBORunning, localGetPurchaseList } = this.props;

    if (isBORunning) {
      this.refreshPersonalDataInternal(localGetPurchaseList);
    }
  };

  refreshViewingHistory = () => {
    const { localGetViewingHistory } = this.props;

    this.refreshPersonalDataInternal(localGetViewingHistory);
  };

  refreshPlayingHistory = () => {
    const { localGetPlayingHistory } = this.props;

    this.refreshPersonalDataInternal(localGetPlayingHistory);
  };

  refreshWishList = () => {
    const { isBORunning, isTVEnabled, localGetFavoriteList, localGetWishlist } = this.props;
    const {
      abortController: { signal },
    } = this;

    // TV wishlist
    if (isTVEnabled) {
      this.refreshPersonalDataInternal(localGetWishlist);
    }

    // VOD wishlist
    if (isBORunning) {
      localGetFavoriteList(signal);
    }
  };

  refreshParentalControl = () => {
    const { localGetParentalControl } = this.props;

    // Parental control data from personal data is only used if the feature is enabled and user profiles feature is disabled
    if (ParentalControlManager.isFeatureDisabled || ProfilesManager.isEnabled) {
      return;
    }

    this.refreshPersonalDataInternal(localGetParentalControl);
  };

  refreshPersonalDataInternal = (localFunction: (signal?: AbortSignal) => Promise<void>): void => {
    const {
      abortController: { signal },
    } = this;

    localFunction(signal).catch((error) => ignoreIfAborted(signal, error));
  };

  clearPurchaseList = (): void => {
    const { localClearPurchaseList } = this.props;

    localClearPurchaseList();
  };

  clearViewingHistory = (): void => {
    const { localClearViewingHistory } = this.props;

    localClearViewingHistory();
  };

  clearPlayingHistory = (): void => {
    const { localClearPlayingHistory } = this.props;

    localClearPlayingHistory();
  };

  clearWishList = (): void => {
    const { localClearFavoriteList, localClearWishList } = this.props;

    localClearWishList();
    localClearFavoriteList();
  };

  clearNpvr = (): void => {
    const { isNpvrEnabled, isRegisteredAsGuest, localClearExistingRecordings, localClearFutureRecordings, localClearScheduledRecordings } = this.props;

    if (isNpvrEnabled && !isRegisteredAsGuest) {
      localClearExistingRecordings();
      localClearFutureRecordings();
      localClearScheduledRecordings();
    }
  };

  showKeyboardShortcuts = (): void => {
    const { showKeyboardShortcuts } = this.props;

    showKeyboardShortcuts();
  };

  showLegalNotice = (): void => {
    const { showLegalNotice } = this.props;

    showLegalNotice();
  };

  openCard = (cardData: CARD_DATA_MODAL_TYPE) => {
    const { showCard } = this.props;

    showCard(cardData);
  };

  refreshPersonalData = () => {
    const { isRegisteredAsGuest } = this.props;
    const { refreshTimer } = this;

    if (refreshTimer || isRegisteredAsGuest) {
      return;
    }

    // Debouncing
    this.refreshTimer = setTimeout(() => {
      this.refreshTimer = null;
      this.refreshPurchaseList();
      this.refreshViewingHistory();
      this.refreshWishList();
      this.refreshParentalControl();
      this.refreshPlayingHistory();
    }, REFRESH_TIMEOUT);
  };

  openConsentSettings: () => void = () => DidomiWrapper.manageConsent();

  resetConsent: () => void = () => DidomiWrapper.resetConsent();

  handleDidomiReady: (didomi: DidomiSDKInstance) => void = (didomi) => {
    DidomiWrapper.initialize(didomi);

    // User consent is needed to initialize Mediametrie
    Nielsen.initialize();
  };

  render(): React.Node {
    const { didomiApiKey, didomiNoticeId } = this.props;
    const { isReady } = this.state;

    return (
      <>
        {didomiApiKey && didomiNoticeId ? <DidomiSDK apiKey={didomiApiKey} gdprAppliesGlobally iabVersion={2} noticeId={didomiNoticeId} onReady={this.handleDidomiReady} /> : null}
        <MainLayout hideContent={!isReady} />
      </>
    );
  }
}

const mapStateToProps = (state: CombinedReducers): ReduxMainReducerStateType => {
  return {
    dataCollectionColdUrl: state.netgemApi.dataCollectionColdUrl,
    dataCollectionHotUrl: state.netgemApi.dataCollectionHotUrl,
    defaultSubtitles: state.appConfiguration.defaultSubtitles,
    didomiApiKey: state.appConfiguration.configuration.didomiApiKey ?? '',
    didomiNoticeId: state.appConfiguration.configuration.didomiNoticeId ?? '',
    focusedAvenueIndex: state.ui.navigationStack[state.ui.navigationStack.length - 1]?.index ?? -1,
    hubMaxAge: state.ui.hubMaxAge,
    isBORunning: state.appConfiguration.isBORunning,
    isNpvrEnabled: state.appConfiguration.features[FEATURE_NPVR] && state.npvr.isEnabled,
    isRegisteredAsGuest: state.appRegistration.registration === RegistrationType.RegisteredAsGuest,
    isTVEnabled: state.appConfiguration.features[FEATURE_TV],
  };
};

const mapDispatchToProps = (dispatch: Dispatch): ReduxMainDispatchToPropsType => {
  return {
    localClearExistingRecordings: () => dispatch(updateExistingRecordings({}, null, [])),
    localClearFavoriteList: () => dispatch(clearBOFavoriteList()),
    localClearFutureRecordings: () =>
      dispatch(
        updateFutureRecordings({}, null, [], {
          current: DEFAULT_RECORDINGS_STATUS_ITEM,
          expectedError: DEFAULT_RECORDINGS_STATUS_ITEM,
          expectedSuccess: DEFAULT_RECORDINGS_STATUS_ITEM,
          maxQuota: DEFAULT_RECORDINGS_STATUS_ITEM,
        }),
      ),
    localClearPlayingHistory: (): void => dispatch(updateWholePlayingHistory({}, null)),
    localClearPurchaseList: (): void => dispatch(clearBOPurchaseList()),
    localClearScheduledRecordings: (): void => dispatch(updateScheduledRecordings({}, {}, null)),
    localClearViewingHistory: (): void => dispatch(updateWholeViewingHistory({}, null)),
    localClearWishList: (): void => dispatch(updateWishlist([], null)),
    localGetAllRecordings: (signal?: AbortSignal): Promise<void> => dispatch(getAllRecordings(signal)),
    localGetAllScheduledRecordings: (signal?: AbortSignal): Promise<void> => dispatch(getAllScheduledRecordings(signal)),
    localGetFavoriteList: (signal?: AbortSignal): Promise<void> => dispatch(getFavoriteList(signal)),
    localGetParentalControl: (signal?: AbortSignal): Promise<void> => dispatch(getParentalControl(signal)),
    localGetPlayingHistory: (signal?: AbortSignal): Promise<void> => dispatch(getPlayingHistory(signal)),
    localGetPurchaseList: (signal?: AbortSignal): Promise<void> => dispatch(getPurchaseList(signal)),
    localGetViewingHistory: (signal?: AbortSignal): Promise<void> => dispatch(getViewingHistory(signal)),
    localGetWishlist: (signal?: AbortSignal): Promise<void> => dispatch(getWishlist(signal)),
    localSendV8HubRequest: (): Promise<void> => dispatch(sendV8HubRequest()),
    localShowProfilesModal: (): void => dispatch(showProfilesModal()),
    localShowProfileSplashscreenModal: (): void => dispatch(showProfileSplashscreenModal()),
    localUpdateDeeplink: (deeplink: string): void => dispatch(setDeeplink(deeplink)),
    localUpdateSetting: (setting?: Setting, value: SettingValue): void => dispatch(updateSetting(setting, value)),
    showCard: (cardData: CARD_DATA_MODAL_TYPE): void => dispatch(showCardModal(cardData)),
    showKeyboardShortcuts: (): void => dispatch(showKeyboardShortcutsModal()),
    showLegalNotice: (): void => dispatch(showLegalNoticeModal()),
  };
};

const Main: React.ComponentType<MainPropType> = connect(mapStateToProps, mapDispatchToProps)(MainView);

export default Main;
